import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClients } from "@/context/ClientContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, PlatformBadge, STATUS_LABEL } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Upload, CalendarDays, FolderOpen, Plug, Eye, Zap, Hand, TrendingUp, AlertTriangle, Building2, Trash2, Activity, ShieldOff, ShieldCheck, RefreshCw } from "lucide-react";

const JOB_STATE_LABEL = {
  queued: "In Warteschlange", claimed: "In Arbeit", confirmed: "Bestätigt",
  failed_retryable: "Wiederholung", failed_final: "Endgültig fehlgeschlagen",
  outcome_unknown: "Prüfen (unklar)",
};

const WorkerCard = ({ w, onRun, busy }) => {
  const hb = w?.heartbeat;
  const stale = hb?.last_tick ? (Date.now() - new Date(hb.last_tick).getTime()) > 120000 : true;
  const jobs = w?.jobs || {};
  const total = Object.values(jobs).reduce((a, b) => a + b, 0);
  return (
    <Card data-testid="worker-status-card" className="p-5 rounded-md border-slate-200 shadow-none">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={16} className={stale ? "text-red-500" : "text-emerald-600"} />
          <span className="overline">Scheduler / Worker</span>
          <span data-testid="worker-heartbeat-dot" className={`h-2 w-2 rounded-full ${stale ? "bg-red-500" : "bg-emerald-500 animate-pulse"}`} />
        </div>
        <Button size="sm" variant="outline" disabled={busy} onClick={onRun} data-testid="worker-run-btn" className="rounded-full h-8">
          <RefreshCw size={13} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> Lauf auslösen
        </Button>
      </div>
      <div className="flex items-center gap-2 mb-4">
        {w?.posting_enabled ? (
          <span data-testid="killswitch-on" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><ShieldCheck size={13} /> Posting aktiv</span>
        ) : (
          <span data-testid="killswitch-off" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full"><ShieldOff size={13} /> Kill-Switch aktiv (Dry-Run)</span>
        )}
        <span className="text-[11px] text-slate-400 font-mono">
          {hb?.last_tick ? `letzter Tick ${new Date(hb.last_tick).toLocaleTimeString("de-DE")}` : "kein Heartbeat"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-2 col-span-2 bg-slate-50">
          <span className="text-sm font-medium">Jobs gesamt</span>
          <span className="font-mono font-semibold" data-testid="worker-jobs-total">{total}</span>
        </div>
        {Object.entries(jobs).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-2">
            <span className="text-xs text-slate-600">{JOB_STATE_LABEL[k] || k}</span>
            <span className="font-mono text-sm font-semibold">{v}</span>
          </div>
        ))}
        <div className="col-span-2 border rounded-sm px-3 py-2 border-amber-200 bg-amber-50 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={13} /> Bitte prüfen</span>
            <span className="font-mono text-sm font-semibold text-amber-800" data-testid="worker-needs-review">{w?.needs_review ?? 0}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-amber-700">
            <span>Drive-Sync-Fehler</span><span className="font-mono" data-testid="worker-drive-errors">{w?.drive_sync_errors ?? 0}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-amber-700">
            <span>Fehlende Drive-Dateien</span><span className="font-mono" data-testid="worker-drive-missing">{w?.drive_missing ?? 0}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const StatCard = ({ label, value, icon: Icon, sub, testid }) => (
  <Card data-testid={testid} className="p-5 rounded-md border-slate-200 shadow-none hover:shadow-sm transition-shadow duration-150">
    <div className="flex items-center justify-between">
      <div className="overline">{label}</div>
      <Icon size={16} className="text-slate-400" />
    </div>
    <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const { current } = useClients();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [worker, setWorker] = useState(null);
  const [workerBusy, setWorkerBusy] = useState(false);

  const loadStats = () => { if (current) api.get(`/posts/stats?client_id=${current.id}`).then(({ data }) => setStats(data)); };
  const loadPosts = () => { if (current) api.get(`/posts?client_id=${current.id}`).then(({ data }) => setPosts(data)); };
  const loadWorker = () => { if (current) api.get(`/worker/status?client_id=${current.id}`).then(({ data }) => setWorker(data)).catch(() => {}); };
  const runWorker = async () => {
    setWorkerBusy(true);
    try {
      const { data } = await api.post("/queue/run", { client_id: current.id });
      const w = data.worker || {};
      toast.success(w.skipped === "posting_disabled"
        ? `${data.enqueued} Jobs eingereiht · Kill-Switch aktiv (Dry-Run)`
        : `Worker-Lauf: ${data.enqueued} eingereiht, ${w.processed ?? 0} verarbeitet`);
      loadWorker(); loadStats(); loadPosts();
    } catch (e) { toast.error(apiError(e)); } finally { setWorkerBusy(false); }
  };
  useEffect(() => {
    if (!current) return;
    loadStats();
    api.get(`/analytics/overview?client_id=${current.id}`).then(({ data }) => setAnalytics(data));
    loadPosts();
    loadWorker();
    setSelected([]);
    const iv = setInterval(loadWorker, 30000);
    return () => clearInterval(iv);
  }, [current]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === posts.length ? [] : posts.map((p) => p.id));
  const deleteSelected = async () => {
    if (!selected.length || !window.confirm(`${selected.length} Posts wirklich löschen?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts/bulk-delete", { client_id: current.id, ids: selected });
      toast.success(`${data.deleted} Posts gelöscht`);
      setSelected([]); loadPosts(); loadStats();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!current) return <div className="text-slate-400">Kein Mandant ausgewählt.</div>;

  const bs = stats?.by_status || {};
  const bm = stats?.by_mode || {};

  return (
    <div>
      <div data-testid="active-client-banner" className="mb-5 flex items-center gap-4 bg-[#0A0A0A] text-white rounded-lg px-6 py-5">
        <div className="h-11 w-11 rounded-md bg-white/10 flex items-center justify-center shrink-0">
          <Building2 size={22} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">Aktiver Mandant</div>
          <div className="font-display text-2xl sm:text-4xl font-extrabold leading-tight truncate">{current.name}</div>
        </div>
      </div>

      <PageHeader
        overline={current.name}
        title="Übersicht"
        subtitle="Status deiner Kampagnen, Queue und Kanäle auf einen Blick."
        actions={
          <Button data-testid="dashboard-import-cta" onClick={() => navigate("/import")}
            className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold">
            <Upload size={16} className="mr-2" /> Bulk-Import starten
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-total" label="Posts gesamt" value={stats?.total ?? "–"} icon={CalendarDays} sub={`${bs.veroeffentlicht || 0} veröffentlicht`} />
        <StatCard testid="stat-ready" label="Bereit / Geplant" value={(bs.bereit || 0) + (bs.geplant || 0)} icon={TrendingUp} sub="in der Queue" />
        <StatCard testid="stat-blocked" label="Blockiert / Fehler" value={(bs.blockiert || 0) + (bs.fehler || 0)} icon={AlertTriangle} sub="benötigen Aufmerksamkeit" />
        <StatCard testid="stat-reach" label="Reichweite (30T)" value={analytics?.totals?.reach ? Intl.NumberFormat("de-DE").format(analytics.totals.reach) : "–"} icon={Eye} sub="Dummy-/Importdaten" />
      </div>

      <div className="mb-6">
        <WorkerCard w={worker} onRun={runWorker} busy={workerBusy} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Queue by mode */}
        <Card className="p-5 rounded-md border-slate-200 shadow-none lg:col-span-1">
          <div className="overline mb-4">Betriebsmodi der Queue</div>
          <div className="space-y-3">
            {[["simulation", "Simulation", Eye, "text-purple-600"], ["assisted", "Assistiert", Hand, "text-blue-600"], ["api", "API-Automatik", Zap, "text-emerald-600"], ["manual", "Manuell / extern", Hand, "text-slate-600"]].map(([k, label, Icon, col]) => (
              <div key={k} className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <Icon size={16} className={col} />
                  <span className="text-sm font-medium">{label}</span>
                </div>
                <span className="font-mono font-semibold">{bm[k] || 0}</span>
              </div>
            ))}
          </div>
          <div className="overline mt-6 mb-3">Status-Verteilung</div>
          <div className="space-y-1.5">
            {Object.keys(STATUS_LABEL).map((s) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <StatusBadge status={s} />
                <span className="font-mono text-slate-600">{bs[s] || 0}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Posts: select all & delete */}
        <Card className="p-5 rounded-md border-slate-200 shadow-none lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={posts.length > 0 && selected.length === posts.length} onCheckedChange={toggleAll} data-testid="select-all-posts" />
              <span className="overline">{selected.length > 0 ? `${selected.length} markiert` : `Posts (${posts.length})`}</span>
            </label>
            <div className="flex items-center gap-2">
              {selected.length > 0 && (
                <Button size="sm" variant="destructive" disabled={busy} onClick={deleteSelected} data-testid="delete-selected-btn" className="rounded-full h-8">
                  <Trash2 size={14} className="mr-1.5" /> {selected.length} löschen
                </Button>
              )}
              <button className="text-xs font-semibold text-slate-500 hover:text-black" onClick={() => navigate("/plan")}>Plan →</button>
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto">
            {posts.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Noch keine Posts. Starte einen Bulk-Import.</div>}
            {posts.map((p) => (
              <div key={p.id} data-testid={`dashboard-post-${p.id}`} className={`flex items-center gap-3 py-2.5 ${selected.includes(p.id) ? "bg-slate-50" : ""}`}>
                <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`select-post-${p.id}`} />
                <PlatformBadge platform={p.platform} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{p.caption || <span className="text-slate-400">— keine Caption —</span>}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{p.account_handle} · Serie {p.series || "–"} · {p.scheduled_at ? new Date(p.scheduled_at).toLocaleString("de-DE") : "kein Termin"}</div>
                </div>
                <StatusBadge post={p} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[["Redaktionsplan", CalendarDays, "/plan"], ["Medienbibliothek", FolderOpen, "/media"], ["Social Accounts", Plug, "/accounts"], ["Analyse", TrendingUp, "/analyse"]].map(([label, Icon, to]) => (
          <button key={to} onClick={() => navigate(to)} className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-md hover:border-slate-400 hover:-translate-y-[1px] transition-transform duration-150 text-left">
            <Icon size={18} className="text-slate-500" />
            <span className="font-semibold text-sm">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
